import React from "react";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardText,
  CardTitle,
  Col,
  Row,
} from "reactstrap";
import { gql } from "apollo-boost";
import { useQuery } from "@apollo/react-hooks";
import { IsErrorOrLoading } from "../utils/IsErrorOrLoading";
import { ReactComponent as TransactionIcon } from "../blockchainOverview/lastTenTransactionsTable/icons/transaction.svg";
import { ReactComponent as SecondSignatureIcon } from "../blockchainOverview/lastTenTransactionsTable/icons/secondSignature.svg";
import { ReactComponent as DelegateRegistrationIcon } from "../blockchainOverview/lastTenTransactionsTable/icons/delegateRegistration.svg";
import { ReactComponent as VoteIcon } from "../blockchainOverview/lastTenTransactionsTable/icons/vote.svg";
import { ReactComponent as MultisigIcon } from "../blockchainOverview/lastTenTransactionsTable/icons/multisig.svg";
import { ReactComponent as UnsupportedIcon } from "../blockchainOverview/lastTenTransactionsTable/icons/unsupported.svg";
import { beddowsToDecimal } from "../utils/lisk/utils/lisk/beddowsToDecimal";
import moment from "moment";
import { fromLiskEpoch } from "../utils/lisk/utils/lisk/time";
import { Link, useParams } from "react-router-dom";
import { hexToString } from "../utils/strings/strings";
import { BlockHeightContext } from "../../UI/layouts/BaseLayout";
import { VotersUnvoters } from "../../UI/components/VotersUnvoters";
import { sanitizeString } from "../utils/strings/censor";

interface Props {}

const TRANSACTION_INFO_QUERY = gql`
  query transctionByID($txId: String) {
    transactions_public(limit: 1, offset: 0, where: { id: { _eq: $txId } }) {
      id
      type
      timestamp
      sender {
        address
        username
        affiliation
      }
      senderId
      asset
      amount
      fee
      recipientId
      recipient {
        address
        affiliation
        username
      }
      transferData
      blockId
      block {
        id
        height
        timestamp
        delegate {
          username
        }
      }
      sender_tag {
        tag
      }
      recipient_tag {
        tag
      }
    }
  }
`;
const BLOCK_HEIGHT_QUERY = gql`
  query newBlocks {
    blocks_public(limit: 1, order_by: { height: desc }) {
      id
      height
    }
  }
`;
const ALL_DELEGATES_QUERY = gql`
  query allDelegates {
    accounts_public(
      where: { username: {}, _not: { username: { _eq: "null" } } }
    ) {
      username
      publicKey
    }
  }
`;

export const TransactionDetail: React.FC<Props> = ({}) => {
  let { txId } = useParams();
  const { data: txData, loading: txLoading, error: txError } = useQuery(
    TRANSACTION_INFO_QUERY,
    {
      variables: {
        txId,
      },
      fetchPolicy: "no-cache",
    }
  );
  const {
    data: blockData,
    loading: blockLoading,
    error: blockError,
  }: any = useQuery(BLOCK_HEIGHT_QUERY);
  const {
    data: allDelegatesData,
    loading: allDelegatesLoading,
    error: allDelegatesError,
  } = useQuery(ALL_DELEGATES_QUERY);

  if (
    txLoading ||
    txError ||
    blockLoading ||
    blockError ||
    allDelegatesLoading ||
    allDelegatesError
  ) {
    return (
      <div className="content">
        <IsErrorOrLoading
          error={!!txError || !!blockError || !!allDelegatesError}
          title={"transactions"}
        />
      </div>
    );
  }

  const delegates = [...allDelegatesData.accounts_public];
  const tx = txData.transactions_public[0];
  if (!tx || !tx.id) {
    return (
      <div className="content">
        <Card>
          <CardHeader>
            <CardTitle tag="h4">Transaction detail</CardTitle>
          </CardHeader>
          <CardBody>Oh no! No transaction found!</CardBody>
        </Card>
      </div>
    );
  }

  const blockHeight =
    blockData &&
    blockData.blocks_public &&
    blockData.blocks_public[0] &&
    blockData.blocks_public[0].height
      ? blockData.blocks_public[0].height
      : 0;
  const confirmations = blockHeight - +tx.block.height;
  const txTypeBadge = (label: string) => {
    return (
      <div>
        <Badge
          className={"badge-info"}
          style={{
            fontWeight: 400,
            marginTop: 15,
          }}
        >
          {label}
        </Badge>
      </div>
    );
  };
  const iconType = () => {
    switch (tx.type) {
      case 0:
        return (
          <div style={{ marginBottom: 20 }}>
            <TransactionIcon
              fill={
                confirmations >= 30
                  ? "green"
                  : document.body.classList.contains("white-content")
                  ? "black"
                  : "white"
              }
              width={40}
              height={40}
              data-tip={`${confirmations} confirmations`}
            />
            {txTypeBadge("Normal transaction")}
          </div>
        );
      case 1:
        return (
          <div style={{ marginBottom: 20 }}>
            <SecondSignatureIcon fill={"green"} width={40} height={40} />
            {txTypeBadge("Second signature")}
          </div>
        );
      case 2:
        return (
          <div style={{ marginBottom: 20 }}>
            <DelegateRegistrationIcon fill={"green"} width={40} height={40} />
            {txTypeBadge("Delegate registration")}
          </div>
        );
      case 3:
        return (
          <div style={{ marginBottom: 20 }}>
            <VoteIcon fill={"green"} width={40} height={40} />
            {txTypeBadge("Vote transaction")}
          </div>
        );
      case 4:
        return (
          <div style={{ marginBottom: 20 }}>
            <MultisigIcon fill={"green"} width={40} height={40} />
            {txTypeBadge("Multisig registration")}
          </div>
        );
      default:
        return (
          <div style={{ marginBottom: 20 }}>
            <UnsupportedIcon fill={"green"} width={40} height={40} />
            {txTypeBadge("Unknown type")}
          </div>
        );
    }
  };
  const realRecipient = (type: number) => {
    switch (type) {
      case 0:
        return (
          <Link to={`/account/${tx.recipientId}`}>
            <strong>
              {(tx.recipient && tx.recipient.username) ||
                (tx.recipient_tag && tx.recipient_tag.tag) ||
                tx.recipientId}
            </strong>
          </Link>
        );
      case 1:
        return <strong>Second signature creation</strong>;
      case 2:
        return <strong>Delegate registration</strong>;
      case 3:
        return <strong>Delegate vote</strong>;
      case 4:
        return <strong>Multisig registration</strong>;
      default:
        return <strong>Unsupported transaction</strong>;
    }
  };
  const TXMetadata = () => {
    if (tx.type === 0 && tx.transferData) {
      const sanitizedData = sanitizeString(hexToString(tx.transferData));
      return (
        <>
          <Col md={2}>TX data:</Col>
          <Col md={10}>
            <strong>{sanitizedData}</strong>
          </Col>
        </>
      );
    }
    if (
      tx.type === 2 &&
      tx.asset &&
      tx.asset.delegate &&
      tx.asset.delegate.username
    ) {
      return (
        <>
          <Col md={4} />
          <Col md={2}>Registered delegate:</Col>
          <Col md={4}>
            <strong>{tx.asset.delegate.username}</strong>
          </Col>
          <Col md={2} />
        </>
      );
    }
    if (tx.type === 3 && tx.asset && tx.asset.votes) {
      const votes = tx.asset.votes.map((pk: any) => {
        const name = delegates.find((d) => {
          return d.publicKey === pk.slice(1, pk.length);
        });
        name.kind = pk.slice(0, 1) === "+" ? "vote" : "unvote";
        return { ...name };
      });
      let plusVotes = 0;
      for (let i in votes) if (votes[i].kind == "vote") plusVotes++;
      const txVotes = votes
        .map((v: any) => (v.kind === "vote" ? v.username : null))
        .filter((a: any) => a !== null)
        .join(" - ");
      const txUnvotes = votes
        .map((v: any) => (v.kind === "unvote" ? v.username : null))
        .filter((a: any) => a !== null)
        .join(" - ");
      const mdVotes = Math.floor((plusVotes * 100) / votes.length / 10);
      return (
        <>
          <Col md={11} style={{ marginLeft: "12px" }}>
            <VotersUnvoters
              mdVotes={mdVotes}
              txVotes={txVotes}
              txUnvotes={txUnvotes}
            ></VotersUnvoters>
          </Col>
        </>
      );
    }
    if (tx.type === 4) {
      return (
        <>
          <Col md={2}>Raw asset:</Col>
          <Col md={10}>
            <pre>{JSON.stringify(tx.asset, null, 2)}</pre>
          </Col>
        </>
      );
    }

    return (
      <Col md={12} className={"text-center"}>
        No metadata included in this transaction
      </Col>
    );
  };

  return (
    <div className="content">
      <Card>
        <CardHeader>
          <CardTitle tag="h4">Transaction detail:</CardTitle>
        </CardHeader>
        <CardBody
          style={{
            color: document.body.classList.contains("white-content")
              ? "black"
              : "#efefef",
          }}
        >
          <Row>
            <Col md={2} className={"text-center"}>
              {iconType()}
            </Col>
            <Col md={10}>
              <Row>
                <Col md={3}>ID:</Col>
                <Col md={3}>
                  <strong>{tx.id}</strong>
                </Col>
                <Col md={3}>Amount:</Col>
                <Col md={3}>
                  <Badge
                    className={"badge-dark"}
                    style={{ fontSize: "medium" }}
                  >
                    {+beddowsToDecimal(tx.amount).toLocaleString()} Ⱡ
                  </Badge>
                </Col>
              </Row>
              <Row>
                <Col md={3}>Sender:</Col>
                <Col md={3}>
                  <strong>
                    <Link to={`/account/${tx.senderId}`}>
                      {(tx.sender && tx.sender.username) ||
                        (tx.sender_tag && tx.sender_tag.tag) ||
                        tx.senderId}
                    </Link>
                  </strong>
                </Col>
                <Col md={3}>Recipient:</Col>
                <Col md={3}>{realRecipient(tx.type)}</Col>
              </Row>
              <Row>
                <Col md={3}>Fee:</Col>
                <Col md={3}>
                  <strong>
                    {+beddowsToDecimal(tx.fee).toLocaleString()} Ⱡ
                  </strong>
                </Col>
                <Col md={3}>Confirmations:</Col>
                <Col md={3}>
                  <strong>{confirmations} confirmations</strong>
                </Col>
              </Row>
              <Row>
                <Col md={3}>TX timestamp:</Col>
                <Col md={3}>
                  <strong>
                    {moment(fromLiskEpoch(+tx.timestamp)).format(
                      "D/MM/YYYY - HH:mm:ss"
                    )}
                  </strong>
                </Col>
                <Col md={3}>Block timestamp:</Col>
                <Col md={3}>
                  <strong>
                    {" "}
                    {moment(fromLiskEpoch(tx.block.timestamp)).format(
                      "D/MM/YYYY - HH:mm:ss"
                    )}
                  </strong>
                </Col>
              </Row>
              <Row style={{ marginTop: 25 }}>{TXMetadata()}</Row>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle tag="h4">Present in this block</CardTitle>
        </CardHeader>
        <CardBody
          style={{
            color: document.body.classList.contains("white-content")
              ? "black"
              : "#efefef",
          }}
        >
          <Row>
            <Col md={2} className={"text-center"}>
              <i
                className="tim-icons icon-app"
                style={{ fontSize: "xxx-large" }}
              />
            </Col>
            <Col md={10}>
              <Row>
                <Col md={3}>ID:</Col>
                <Col md={3}>
                  <strong>{tx.block.id}</strong>
                </Col>
                <Col md={3}>Height:</Col>
                <Col md={3}>
                  <strong>{tx.block.height}</strong>
                </Col>
              </Row>
              <Row>
                <Col md={3}>timestamp:</Col>
                <Col md={3}>
                  <strong>
                    {moment(fromLiskEpoch(+tx.block.timestamp)).format(
                      "D/MM/YYYY - HH:mm:ss"
                    )}
                  </strong>
                </Col>
                <Col md={3}>Validated by:</Col>
                <Col md={3}>
                  {tx.block &&
                  tx.block.delegate &&
                  tx.block.delegate.username ? (
                    <Link to={`/account/${tx.block.delegate.username}`}>
                      {tx.block.delegate.username}
                    </Link>
                  ) : (
                    "unknown"
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  );
};
